import React, { useEffect, useState } from 'react';
import c from 'classnames';
import { useParams } from 'react-router-dom';
import { ItemsListLazyLoad, ItemsListSidebar } from '../itemslist';
import {
  batchFetchOp,
  selectItemsListMediaFilter,
  selectItemsListNeedsToBeLoaded,
  selectShowMedia,
  selectShowNsfw,
  selectFloppersDefaultSort,
  selectItemsListParams,
} from '../../ducks';
import { ListType } from '../../apis/dumpert';
import { getTypeOfListFromCookie, pathToCookie } from '../../cookie';
import { Ads, AdScript, createSlotParams } from '../ads';
import { StemSidebarBlock } from '../stemform';
import { StickySidebar } from '../stickysidebar';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentBreakPoint, useItemViewSearchParams } from '../commons';
import { adBreakPoint } from '../../lib/helpers';
import { ScrollToTopButton } from '../scrolltotop';
import { getValidItemId } from '../paths';

const getListTypes = (listType, staticContext, pageNum) => {
  const mainType = listType || getTypeOfListFromCookie('list', staticContext);
  const topFiveType = mainType.startsWith('top') ? ListType.latest : ListType.toppers;
  const dumpType = ListType.dumperttv;
  return {
    mainType: mainType === 'top' || mainType === 'nieuw'

      ? `${mainType}_${pageNum}`
      : mainType, topFiveType, dumpType,
  };
};

export const ItemsListViewPage = ({ staticContext }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const { listType, pageNum, offset, mediaFilter } = params;
  const { selectedId } = useItemViewSearchParams();
  const { mainType, topFiveType, dumpType } = getListTypes(listType, staticContext, pageNum, offset);
  const topFiveLoad = useSelector(state => selectItemsListNeedsToBeLoaded(state, topFiveType));
  const dumpTypeLoad = useSelector(state => selectItemsListNeedsToBeLoaded(state, dumpType));
  const mainTypeLoad = useSelector(state => selectItemsListNeedsToBeLoaded(state, mainType));
  const showNsfw = useSelector(selectShowNsfw);
  const showMedia = useSelector(selectShowMedia);
  const topMedia = useSelector(state => selectItemsListMediaFilter(state, topFiveType));
  const dumpMedia = useSelector(state => selectItemsListMediaFilter(state, dumpType));
  const mainMedia = useSelector(state => selectItemsListMediaFilter(state, mainType));
  const listParams = useSelector(state => selectItemsListParams(state, mainType));
  const currentBreakPoint = useCurrentBreakPoint();
  const [currentShowNsfw, setShowNsfw] = useState(useSelector(selectShowNsfw));
  const actualMediaFilter = pathToCookie(mediaFilter) || showMedia;
  const floppersSorting = useSelector(selectFloppersDefaultSort);
  const [currentFloppersSorting, setFloppersSorting] = useState(useSelector(selectFloppersDefaultSort));

  useEffect(() => {
    const lists = [];
    const nsfwIsUpdated = () => showNsfw !== currentShowNsfw;
    const floppersSortingIsUpdated = () => floppersSorting !== currentFloppersSorting;
    const listParamsUpdated = () =>
      ['listType', 'pageNum', 'offset'].some(k => listParams[k] !== params[k]);

    if (mainTypeLoad || listParamsUpdated() || mainMedia !== actualMediaFilter || nsfwIsUpdated()) {
      lists.push({ type: mainType, params });
    }
    if (topFiveLoad || topMedia !== actualMediaFilter || nsfwIsUpdated()) {
      lists.push({ type: topFiveType, params: {} });
    }
    if (dumpTypeLoad || dumpMedia !== actualMediaFilter || nsfwIsUpdated()) {
      lists.push({ type: dumpType, params });
    }
    if (listType === 'floppers' || nsfwIsUpdated() || floppersSortingIsUpdated()) {
      lists.push({ type: listType, params: { ...params, sorting: floppersSorting } });
    }
    if (lists.length || nsfwIsUpdated()) {
      dispatch(batchFetchOp({ items: [], lists }));
    }
    setFloppersSorting(floppersSorting);
    setShowNsfw(showNsfw);

  }, [listParams.pageNum, listParams.listType, listParams.offset,
    listType, pageNum, offset, actualMediaFilter, showNsfw, floppersSorting]);

  return (
    <AdScript adunit="homepage" blockads={!!selectedId} >
      {['XL', 'L', 'M', 'S'].includes(adBreakPoint(currentBreakPoint)) && (
        <Ads {...createSlotParams('leaderboardwallpaper', 't1', true, false, true)}/>
      )}
      {['XS'].includes(adBreakPoint(currentBreakPoint)) && (
        <Ads {...createSlotParams('rectangle', 't1', true, false, true)} />
      )}
      {['XS', 'S'].includes(adBreakPoint(currentBreakPoint)) && (
        <StemSidebarBlock mobile/>
      )}      
      <div className={c(
        'grid',
        { ['three']: ['M'].includes(adBreakPoint(currentBreakPoint)) },
        { ['four']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) },
      )}>
        <main className={c(
          { ['two-third']: ['M'].includes(adBreakPoint(currentBreakPoint)) },
          { ['three-fourth']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) },
        )}>
          <ItemsListLazyLoad
            typeOfList={mainType}
            params={params}
            currentAdBreakPoint={adBreakPoint(currentBreakPoint)}
          />
        </main>
        {['M', 'L', 'XL'].includes(adBreakPoint(currentBreakPoint)) && (
          <aside
            className={c(
              { ['third']: ['M'].includes(adBreakPoint(currentBreakPoint)) },
              { ['fourth']: ['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) },
            )}
            data-type={'sidebar'}
          >
            <StickySidebar>            
              {['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) && (
                <Ads {...createSlotParams('rectangle', 't2')} />
              )}

              <StemSidebarBlock />
              <ItemsListSidebar
                typeOfList={topFiveType}
                numOfItems={5}
              />
              <ItemsListSidebar
                typeOfList={dumpType}
                numOfItems={3}
              />
              {['L', 'XL'].includes(adBreakPoint(currentBreakPoint)) && (
                <Ads {...createSlotParams('rectangleinterscroller1', 'b1')}/>
              )}
            </StickySidebar>
          </aside>
        )}
      </div>
      <ScrollToTopButton />
    </AdScript>
  );
};

ItemsListViewPage.serverFetch = ({ params }, staticContext) => {
  const itemId = getValidItemId(staticContext.request.query.selectedId);
  const { mainType, topFiveType, dumpType } =
    getListTypes(params.listType, staticContext, params.pageNum, params.offset);
  return batchFetchOp({
    items: itemId ? [itemId] : [],
    lists: [
      { type: mainType, params },
      { type: topFiveType, params: {} },
      { type: dumpType, params },
    ],
  });
};
